/**
 * globes - a set of models of the earth, each having their own kind of projection and onscreen behavior.
 *
 * Copyright (c) 2014 Cameron Beccario
 * The MIT License - http://opensource.org/licenses/MIT
 *
 * https://github.com/cambecc/earth
 */

import _ from "underscore";
import µ from "./micro";
import * as d3 from "d3";
import d3v3 from "../../lib/d3/3.3.10/d3.repack";
import {geoMollweide, geoPolyhedralWaterman, geoWinkel3} from "../../lib/d3.geo.projection/2.4.0/d3-geo-projection.repack";
import {geoPolyhedralVoronoi} from "../../lib/d3.geo.polygon/1.6.1/d3-geo-polygon.repack"

export default (function() {

    var abs$1 = Math.abs;
    var atan$1 = Math.atan;
    var atan2$1 = Math.atan2;
    var cos$1 = Math.cos;
    var max$1 = Math.max;
    var min$1 = Math.min;
    var sin$1 = Math.sin;

    var epsilon$1 = 1e-6;
    var pi$1 = Math.PI;
    //var halfPi$1 = pi$1 / 2;
    var halfPi = Math.PI;
    var sqrt1_2 = Math.SQRT1_2;
    var degrees$1 = 180 / pi$1;
    var radians$1 = pi$1 / 180;

    var phi1 = atan$1(sqrt1_2) * degrees$1;

    var sqrt = Math.sqrt;
    var pi = Math.PI;
    var degrees = 180 / pi;

    function acos(x) {
      return x > 1 ? 0 : x < -1 ? pi : Math.acos(x);
    }

    function asin(x) {
      return x > 1 ? halfPi : x < -1 ? -halfPi : Math.asin(x);
    }

    /**
     * @returns {Array} rotation of globe to current position of the user. Aside from asking for geolocation,
     *          which user may reject, there is not much available except timezone. Better than nothing.
     */
    function currentPosition() {
        var λ = µ.floorMod(new Date().getTimezoneOffset() / 4, 360);  // 24 hours * 60 min / 4 === 360 degrees
        return [λ, 0];
    }

    function ensureNumber(num, fallback) {
        return _.isFinite(num) || num === Infinity || num === -Infinity ? num : fallback;
    }

    /**
     * @param bounds the projection bounds: [[x0, y0], [x1, y1]]
     * @param view the view bounds {width:, height:}
     * @returns {Object} the projection bounds clamped to the specified view.
     */
    function clampedBounds(bounds, view) {
        var upperLeft = bounds[0];
        var lowerRight = bounds[1];
        var x = Math.max(Math.floor(ensureNumber(upperLeft[0], 0)), 0);
        var y = Math.max(Math.floor(ensureNumber(upperLeft[1], 0)), 0);
        var xMax = Math.min(Math.ceil(ensureNumber(lowerRight[0], view.width)), view.width - 1);
        var yMax = Math.min(Math.ceil(ensureNumber(lowerRight[1], view.height)), view.height - 1);
        return {x: x, y: y, xMax: xMax, yMax: yMax, width: xMax - x + 1, height: yMax - y + 1};
    }

    /**
     * Returns a globe object with standard behavior. At least the newProjection method must be overridden to
     * be functional.
     */
    function standardGlobe() {
        return {
            /**
             * This globe's current D3 projection.
             */
            projection: null,

            /**
             * @param view the size of the view as {width:, height:}.
             * @returns {Object} a new D3 projection of this globe appropriate for the specified view port.
             */
            newProjection: function(view) {
                throw new Error("method must be overridden");
            },

            /**
             * @param view the size of the view as {width:, height:}.
             * @returns {{x: Number, y: Number, xMax: Number, yMax: Number, width: Number, height: Number}}
             *          the bounds of the current projection clamped to the specified view.
             */
            bounds: function(view) {
                return clampedBounds(d3.geoPath().projection(this.projection).bounds({type: "Sphere"}), view);
            },

            /**
             * @param view the size of the view as {width:, height:}.
             * @returns {Number} the projection scale at which the entire globe fits within the specified view.
             */
            fit: function(view) {
                var defaultProjection = this.newProjection(view);
                var bounds = d3.geoPath().projection(defaultProjection).bounds({type: "Sphere"});
                var hScale = (bounds[1][0] - bounds[0][0]) / defaultProjection.scale();
                var vScale = (bounds[1][1] - bounds[0][1]) / defaultProjection.scale();
                return Math.min(view.width / hScale, view.height / vScale) * 0.9;
            },

            /**
             * @param view the size of the view as {width:, height:}.
             * @returns {Array} the projection transform at which the globe is centered within the specified view.
             */
            center: function(view) {
                return [view.width / 2, view.height / 2];
            },

            /**
             * @returns {Array} the range at which this globe can be zoomed.
             */
            scaleExtent: function() {
                return [25, 3000];
            },

            /**
             * Returns the current orientation of this globe as a string. If the arguments are specified,
             * mutates this globe to match the specified orientation string, usually in the form "lat,lon,scale".
             *
             * @param [o] the orientation string
             * @param [view] the size of the view as {width:, height:}.
             */
            orientation: function(o, view) {
                var projection = this.projection, rotate = projection.rotate();
                if (µ.isValue(o)) {
                    var parts = o.split(","), λ = +parts[0], φ = +parts[1], scale = +parts[2];
                    var extent = this.scaleExtent();
                    projection.rotate(_.isFinite(λ) && _.isFinite(φ) ?
                        [-λ, -φ, rotate[2]] :
                        this.newProjection(view).rotate());
                    projection.translate(this.center(view));
                    projection.scale(_.isFinite(scale) ? µ.clamp(scale, extent[0], extent[1]) : this.fit(view));
                    return this;
                }
                return [(-rotate[0]).toFixed(2), (-rotate[1]).toFixed(2), Math.round(projection.scale())].join(",");
            },

            /**
             * Returns an object that mutates this globe's current projection during a drag/zoom operation.
             * Each drag/zoom event invokes the move() method, and when the move is complete, the end() method
             * is invoked.
             *
             * @param startMouse starting mouse position.
             * @param startScale starting scale.
             */
            manipulator: function(startMouse, startScale) {
                var projection = this.projection;
                var sensitivity = 60 / startScale;  // seems to provide a good drag scaling factor
                var rotation = [projection.rotate()[0] / sensitivity, -projection.rotate()[1] / sensitivity];
                var original = projection.precision();
                projection.precision(original * 10);
                return {
                    move: function(mouse, scale) {
                        if (mouse) {
                            var xd = mouse[0] - startMouse[0] + rotation[0];
                            var yd = mouse[1] - startMouse[1] + rotation[1];
                            // let maxRotateY =  Math.max(-89, Math.min(89, -yd * sensitivity));
                            // projection.rotate([xd * sensitivity, maxRotateY, projection.rotate()[2]]);
                            projection.rotate([xd * sensitivity, -yd * sensitivity, projection.rotate()[2]]);
                        }
                        projection.scale(scale);
                    },
                    end: function() {
                        projection.precision(original);
                    }
                };
            },

            /**
             * @returns {Array} the transform to apply, if any, to orient this globe to the specified coordinates.
             */
            locate: function(coord) {
                return null;
            },

            /**
             * Draws a polygon on the specified context of this globe's boundary.
             * @param context a Canvas element's 2d context.
             * @returns the context
             */
            defineMask: function(context) {
                d3.geoPath().projection(this.projection).context(context)({type: "Sphere"});
                return context;
            },

            drawMap: function(context, path, mesh) {
                d3.select("body").style("background", "#000005");

                context.fillStyle = "#303030";
                context.beginPath();
                path({type: "Sphere"});
                context.fill();

                context.strokeStyle = "#FFFFFF";
                context.lineWidth = 1.25;
                context.save();
                context.beginPath();
                context.shadowOffsetX = 4;
                context.shadowOffsetY = 4;
                context.shadowBlur    = 7;
                context.shadowColor   = "black";
                path(mesh.coast);
                context.stroke();

                context.beginPath();
                context.shadowOffsetX = 2;
                context.shadowOffsetY = 2;
                context.shadowBlur    = 7;
                context.shadowColor   = "black";
                path(mesh.lakes);
                context.stroke();
                context.restore();
            },

            drawGraticuleHemisphere: function(context, path){
                var graticule = d3.geoGraticule();
                var hemisphere = d3.geoGraticule().stepMinor([0, 90]).stepMajor([0, 90]);

                context.strokeStyle = "#505050";
                context.lineWidth = 1.0;
                context.beginPath();
                path(graticule());
                context.stroke();

                context.strokeStyle = "#707070";
                context.lineWidth = 1.0;
                context.beginPath();
                path(hemisphere());
                context.stroke();
            },

            drawForeground: function(context, path){
                context.fillStyle = context.strokeStyle = "#000005";
                context.lineWidth = 4;
                context.beginPath();
                path({type: "Sphere"});
                context.stroke();
            },

            drawWing: function(context, projection){},

            quickoverlay: false
        };
    }

    function newGlobe(source, view) {
        var result = _.extend(standardGlobe(), source);
        result.projection = result.newProjection(view);
        return result;
    }

    // ============================================================================================

    function atlantis() {
        return newGlobe({
            newProjection: function() {
                return geoMollweide().rotate([30, -45, 90]).precision(0.1);
            }
        });
    }

    function azimuthalEquidistant() {
        return newGlobe({
            newProjection: function() {
                return d3.geoAzimuthalEquidistant().precision(0.1).rotate([0, -90]).clipAngle(180 - 0.001);
            }
        });
    }

    function conicEquidistant() {
        return newGlobe({
            newProjection: function() {
                return d3.geoConicEquidistant().rotate(currentPosition()).precision(0.1);
            },
            center: function(view) {
                return [view.width / 2, view.height / 2 + view.height * 0.065];
            }
        });
    }

    function equirectangular() {
        return newGlobe({
            newProjection: function() {
                return d3.geoEquirectangular().rotate(currentPosition()).precision(0.1);
            }
        });
    }

    function sliceToArray(arr, i) { 
        return isArray(arr) || iterableToArray(arr, i);
    }

    function iterableToArray(arr, i) { 
        var arr = []; 
        var n = true; 
        var d = false; 
        var e = undefined; 

        for (var i = arr[Symbol.iterator](), s; !(n = (s = i.next()).done); n = true) {
            arr.push(s.value);
            if (i && arr.length === i) break; 
        } 

        return arr; 
    }

    function isArray(arr) { 
        if (Array.isArray(arr)) return arr; 
    }

    function orthographic(view) {
        return newGlobe({
            newProjection: function() {
                return d3v3.geo.orthographic().rotate(currentPosition()).precision(0.1).clipAngle(90);
            },
            drawMap: function(context, path, mesh) {
                d3.select("body").style("background", "#000005");

                context.fillStyle = "#303030";
                context.beginPath();
                path({type: "Sphere"});
                context.fill();

                var proj = path.projection(),
                translate = proj.translate(),
                translateArray = sliceToArray(translate, 2),
                x = translateArray[0],
                y = translateArray[1],
                scale = proj.scale();
                var gradient = context.createRadialGradient(x, y, 0, x, y, scale);
                gradient.addColorStop(0.69, "#303030");
                gradient.addColorStop(0.91, "#202020");
                gradient.addColorStop(0.96, "#000005");
                context.fillStyle = gradient;
                context.fillRect(x - scale, y - scale, scale * 2, scale * 2);

                var graticule = d3.geoGraticule();
                var hemisphere = d3.geoGraticule().stepMinor([0, 90]).stepMajor([0, 90]);

                context.strokeStyle = "#505050";
                context.beginPath();
                context.lineWidth = 1.0;
                path(graticule());
                context.stroke();

                context.strokeStyle = "#707070";
                context.beginPath();
                context.lineWidth = 1.0;
                path(hemisphere());
                context.stroke();

                context.strokeStyle = "#FFFFFF";
                context.save();
                context.beginPath();
                context.lineWidth = 1.25;
                context.shadowOffsetX = 4;
                context.shadowOffsetY = 4;
                context.shadowBlur    = 7;
                context.shadowColor   = "black";
                path(mesh.coast);
                context.stroke();

                context.beginPath();
                context.lineWidth = 1.0;
                context.shadowOffsetX = 2;
                context.shadowOffsetY = 2;
                context.shadowBlur    = 7;
                context.shadowColor   = "black";
                path(mesh.lakes);
                context.stroke();

                context.restore();
            },
            drawGraticuleHemisphere: function(context, path){
            },
            locate: function(coord) {
                return [-coord[0], -coord[1], this.projection.rotate()[2]];
            },
            quickoverlay: true
        });
    }

    function stereographic(view) {
        return newGlobe({
            newProjection: function(view) {
                return d3.geoStereographic()
                    .rotate([-43, -20])
                    .precision(1.0)
                    .clipAngle(180 - 0.0001)
                    .clipExtent([[0, 0], [view.width, view.height]]);
            }
        }, view);
    }

    function waterman() {
        return newGlobe({
            newProjection: function() {
                return geoPolyhedralWaterman().rotate([20, 0]).precision(0.1);
            },
            drawMap: function(context, path, mesh) {
                d3.select("body").style("background", "#000005");

                context.fillStyle = "#303030";
                context.beginPath();
                path({type: "Sphere"});
                context.fill();

                context.strokeStyle = "#FFFFFF";
                context.lineWidth = 1.0;
                context.beginPath();
                path(mesh.coast);
                context.stroke();

                context.beginPath();
                path(mesh.lakes);
                context.stroke();

                var graticule = d3.geoGraticule();
                var hemisphere = d3.geoGraticule().stepMinor([0, 90]).stepMajor([0, 90]);

                context.strokeStyle = "#505050";
                context.lineWidth = 1.0;
                context.beginPath();
                path(graticule());
                context.stroke();

                context.strokeStyle = "#707070";
                context.lineWidth = 1.0;
                context.beginPath();
                path(hemisphere());
                context.stroke();

            },
            drawGraticuleHemisphere: function(context, path){
            },
            drawForeground: function(context, path){
                var canvas = context.canvas, width = canvas.width, height = canvas.height;
                context.fillStyle = context.strokeStyle = "#000005";
                context.lineWidth = 4;
                context.beginPath();
                path({type: "Sphere"});
                context.stroke();
                context.moveTo(0, 0);
                context.lineTo(width, 0);
                context.lineTo(width, height);
                context.lineTo(0, height);
                context.lineTo(0, 0);
                context.fill("evenodd");
            }
        });
    }

    function winkel3() {
        return newGlobe({
            newProjection: function() {
                return geoWinkel3().precision(0.1);
            }
        });
    }
    // function geoCubicN() {
    //     /*
    //      * Cubic map
    //      *
    //      * Implemented for D3.js by Enrico Spinielli (2017) and Philippe Rivière (2017, 2018)
    //      * Rearranged for NIMS (2018)
    //      *
    //      */
    //     var cube = [
    //         [0, phi1], [90, phi1], [180, phi1], [-90, phi1],
    //         [0, -phi1], [90, -phi1], [180, -phi1], [-90, -phi1]
    //     ];
    //
    //     var cube$1 = [
    //         [0, 3, 2, 1], // N
    //         [0, 1, 5, 4],
    //         [3, 0, 4, 7],
    //         [2, 3, 7, 6],
    //         [1, 2, 6, 5],
    //         [4, 5, 6, 7]
    //     ].map(function(face) {
    //         return face.map(function(i) {
    //             return cube[i];
    //         });
    //     });
    //
    //     function cubicN() {
    //         var polygons = {
    //             type: "FeatureCollection",
    //             features: cube$1.map(function(face) {
    //                 face = face.slice();
    //                 face.push(face[0]);
    //                 return {
    //                     geometry: {
    //                         type: "Polygon",
    //                         coordinates: [face]
    //                     }
    //                 };
    //             })
    //         };
    //
    //         var parents = [-1, 0, 0, 0, 0, 4];
    //         var agent = navigator.userAgent.toLowerCase();
    //
    //         if ((navigator.appName === "Netscape" && agent.indexOf("trident") !== -1) || (agent.indexOf("msie") !== -1)) {
    //             // return d3.geoPolyhedralVoronoi()
    //             return geoPolyhedralVoronoi()
    //                 .polygons(polygons)
    //                 .parents(parents)
    //                 .angle(180)
    //                 .scale(96.8737)
    //                 .center([135, 45])
    //                 .rotate([-45, 45, 90]);
    //         } else {
    //             // return d3.geoPolyhedralVoronoi()
    //             return geoPolyhedralVoronoi()
    //                 .polygons(polygons)
    //                 .parents(parents)
    //                 .angle(-45)
    //                 .scale(96.8737)
    //                 .center([135, 45])
    //                 .rotate([-45, 45, 90]);
    //         }
    //     }
    //
    //     // d3.geoCubicN = cubicN;
    //
    //     // return d3.geoCubicN();
    //     return cubicN();
    // }

    // function cubic(view){
    //     return newGlobe({
    //         newProjection: geoCubicN,
    //
    //         drawForeground: function(context, path){
    //             context.fillStyle = context.strokeStyle = "#ffffff";
    //             context.lineWidth = 4;
    //             context.beginPath();
    //             path({type: "Sphere"});
    //             context.stroke();
    //         },
    //
    //         drawWing: function(context, projection, currentScale){
    //             d3.select("body").style("background", "#ffffff");
    //
    //             var offset = 0.0001;
    //
    //             var hedralPoints = [];
    //
    //             hedralPoints = [[0 + offset, 35.264389682754654 - offset],
    //                             [90 - offset, 35.264389682754654 - offset],
    //                             [90 - offset, -35.264389682754654 + offset],
    //                             [0 + offset, -35.264389682754654 + offset],
    //                             [0 + offset, 35.264389682754654 - offset]];
    //
    //             var hedralCoords = [];
    //             var wingProjection = geoCubicN().scale(currentScale).translate(projection([180, 0]));
    //
    //             for (var i=0; i<hedralPoints.length; i++){
    //                 hedralCoords.push(wingProjection(hedralPoints[i]));
    //             }
    //
    //             var startX = hedralCoords[0][0], startY = hedralCoords[0][1],
    //                 width = hedralCoords[1][0] - hedralCoords[0][0], wingHeight = 53 * currentScale / 154, xOffset = 3, yOffset = 2;
    //
    //             context.fillStyle = context.strokeStyle = "#000005";
    //             // context.fillStyle = context.strokeStyle = "#c4c9d1";
    //             context.lineWidth = 0.5;
    //             context.beginPath();
    //
    //             drawWingLine(context,
    //                          startX + width * 0 + xOffset *  1 + wingHeight *  0, startY + width *  0 + yOffset * 1 + wingHeight *  0,
    //                          startX + width * 0 + xOffset *  1 + wingHeight *  1, startY + width *  0 + yOffset * 1 + wingHeight * -1,
    //                          startX + width * 1 + xOffset *  1 + wingHeight * -1, startY + width *  0 + yOffset * 1 + wingHeight * -1,
    //                          startX + width * 1 + xOffset *  0 + wingHeight *  0, startY + width *  0 + yOffset * 1 + wingHeight *  0)
    //
    //             drawWingLine(context,
    //                          startX + width * 0 + xOffset *  1 + wingHeight *  0, startY + width *  1 + yOffset * -1 + wingHeight *  0,
    //                          startX + width * 0 + xOffset *  1 + wingHeight *  1, startY + width *  1 + yOffset * -1 + wingHeight *  1,
    //                          startX + width * 1 + xOffset *  1 + wingHeight * -1, startY + width *  1 + yOffset * -1 + wingHeight *  1,
    //                          startX + width * 1 + xOffset *  0 + wingHeight *  0, startY + width *  1 + yOffset * -1 + wingHeight *  0);
    //
    //             drawWingLine(context,
    //                          startX + width * 2 + xOffset *  1 + wingHeight *  0, startY + width *  0 + yOffset * 1 + wingHeight *  0,
    //                          startX + width * 2 + xOffset *  1 + wingHeight *  1, startY + width *  0 + yOffset * 1 + wingHeight * -1,
    //                          startX + width * 3 + xOffset *  1 + wingHeight * -1, startY + width *  0 + yOffset * 1 + wingHeight * -1,
    //                          startX + width * 3 + xOffset *  0 + wingHeight *  0, startY + width *  0 + yOffset * 1 + wingHeight *  0);
    //
    //             drawWingLine(context,
    //                          startX + width * 1 + xOffset *  1 + wingHeight *  0, startY + width * -1 + yOffset * 1 + wingHeight *  0,
    //                          startX + width * 1 + xOffset *  1 + wingHeight *  1, startY - width *  1 + yOffset * 1 + wingHeight * -1,
    //                          startX + width * 2 + xOffset *  1 + wingHeight * -1, startY - width *  1 + yOffset * 1 + wingHeight * -1,
    //                          startX + width * 2 + xOffset * -1 + wingHeight *  0, startY - width *  1 + yOffset * 1 + wingHeight *  0);
    //
    //             drawWingLine(context,
    //                          startX + width * 2 + xOffset *  1 + wingHeight *  0, startY + width *  1 + yOffset * -1 + wingHeight *  0,
    //                          startX + width * 2 + xOffset *  1 + wingHeight *  1, startY + width *  1 + yOffset * -1 + wingHeight *  1,
    //                          startX + width * 3 + xOffset *  1 + wingHeight * -1, startY + width *  1 + yOffset * -1 + wingHeight *  1,
    //                          startX + width * 3 + xOffset *  0 + wingHeight *  0, startY + width *  1 + yOffset * -1 + wingHeight *  0);
    //
    //             drawWingLine(context,
    //                          startX + width * 3 + xOffset *  1 + wingHeight *  0, startY + width *  1 + yOffset * -1 + wingHeight *  0,
    //                          startX + width * 3 + xOffset *  1 + wingHeight *  1, startY + width *  1 + yOffset * -1 + wingHeight *  1,
    //                          startX + width * 4 + xOffset *  1 + wingHeight * -1, startY + width *  1 + yOffset * -1 + wingHeight *  1,
    //                          startX + width * 4 + xOffset *  0 + wingHeight *  0, startY + width *  1 + yOffset * -1 + wingHeight *  0);
    //
    //             drawWingLine(context,
    //                          startX + width * 4 + xOffset *  -1 + wingHeight *  0, startY + width *  0 + yOffset * 1 + wingHeight *  0,
    //                          startX + width * 4 + xOffset *  -1 + wingHeight *  1, startY + width *  0 + yOffset * 1 + wingHeight *  1,
    //                          startX + width * 4 + xOffset *  -1 + wingHeight *  1, startY + width *  1 + yOffset * 1 + wingHeight * -1,
    //                          startX + width * 4 + xOffset *  -1 + wingHeight *  0, startY + width *  1 + yOffset * 1 + wingHeight *  0);
    //
    //             context.stroke();
    //
    //             function drawWingLine(context, moveToX, moveToY, line1X, line1Y, line2X, line2Y, line3X, line3Y){
    //                 context.moveTo(moveToX, moveToY);
    //                 context.lineTo(line1X, line1Y);
    //                 context.lineTo(line2X, line2Y);
    //                 context.lineTo(line3X, line3Y);
    //             }
    //         }
    //     });
    // }

    // function geoDodecahedralN() {
    //     /*
    //      * Dodecahedral map
    //      *
    //      * Implemented for D3.js
    //      * Rearranged for NIMS (2018)
    //      *
    //      */
    //     function dodecahedralN() {
    //         var A0 = asin(1/sqrt(3)) * degrees,
    //             A1 = acos((sqrt(5) - 1) / sqrt(3) / 2) * degrees,
    //             A2 = 90 - A1,
    //             A3 = acos(-(1 + sqrt(5)) / sqrt(3) / 2) * degrees;
    //
    //         var dodecahedron = [
    //             [[45,A0],[0,A1],[180,A1],[135,A0],[90,A2]],
    //             [[45,A0],[A2,0],[-A2,0],[-45,A0],[0,A1]],
    //             [[45,A0],[90,A2],[90,-A2],[45,-A0],[A2,0]],
    //             [[0,A1],[-45,A0],[-90,A2],[-135,A0],[180,A1]],
    //             [[A2,0],[45,-A0],[0,-A1],[-45,-A0],[-A2,0]],
    //             [[90,A2],[135,A0],[A3,0],[135,-A0],[90,-A2]],
    //             [[45,-A0],[90,-A2],[135,-A0],[180,-A1],[0,-A1]],
    //             [[135,A0],[180,A1],[-135,A0],[-A3,0],[A3,0]],
    //             [[-45,A0],[-A2,0],[-45,-A0],[-90,-A2],[-90,A2]],
    //             [[-45,-A0],[0,-A1],[180,-A1],[-135,-A0],[-90,-A2]],
    //             [[135,-A0],[A3,0],[-A3,0],[-135,-A0],[180,-A1]],
    //             [[-135,A0],[-90,A2],[-90,-A2],[-135,-A0],[-A3,0]]
    //         ];
    //
    //         var polygons = {
    //             type: "FeatureCollection",
    //             features: dodecahedron.map(function(face) {
    //                 face.push(face[0]);
    //                 return {
    //                     geometry: {
    //                         type: "Polygon",
    //                         coordinates: [ face ]
    //                     }
    //                 };
    //             })
    //         };
    //
    //         // return d3.geoPolyhedralVoronoi()
    //         return geoPolyhedralVoronoi()
    //             .parents([-1,0,4,8,1,6,2,11,1,4,5,3])
    //             .angle(72 * 1.5)
    //             .polygons(polygons)
    //             .scale(99.8)
    //             .center([0, 0])
    //             .rotate([210, 0, -32]);
    //     }
    //     // d3.geoDodecahedralN = dodecahedralN;
    //
    //     // return d3.geoDodecahedralN();
    //     return dodecahedralN();
    // }

    // function dodecahedral(view){
    //     return newGlobe({
    //         newProjection: geoDodecahedralN,
    //         drawForeground: function(context, path){
    //             context.fillStyle = context.strokeStyle = "#ffffff";
    //             context.lineWidth = 4;
    //             context.beginPath();
    //             path({type: "Sphere"});
    //             context.stroke();
    //         },
    //
    //         drawWing: function(context, projection, currentScale){
    //             d3.select("body").style("background", "#ffffff");
    //
    //             var offset = 0.0001;
    //
    //             var hedralPoints00 = [[135 - offset, 35.264389682754654],
    //                                   [90, 20.90515744788931 + offset],
    //                                   [45 - offset, 35.264389682754654],
    //                                   [0 + offset, 69.09484255211069],
    //                                   [180 - offset, 69.09484255211069],
    //                                   [135 - offset, 35.264389682754654]];
    //
    //             var hedralPoints17 = [[135 + offset, 35.264389682754654],
    //                                   [180, 69.09484255211069 - offset],
    //                                   [-135 - offset, 35.264389682754654],
    //                                   [-159.09484255211072, 0 + offset],
    //                                   [159.09484255211072, 0 + offset],
    //                                   [135 + offset, 35.264389682754654]];
    //
    //             var hedralPoints13 = [[-(180 - offset), 69.09484255211069],
    //                                   [0 - offset, 69.09484255211069],
    //                                   [-45 - offset, 35.264389682754654],
    //                                   [-90, 20.90515744788931 + offset],
    //                                   [-135 + offset, 35.264389682754654],
    //                                   [-(180 - offset), 69.09484255211069]];
    //
    //             var hedralPoints11 = [[0, 69.09484255211069 - offset],
    //                                   [45 - offset, 35.264389682754654],
    //                                   [20.90515744788931, 0 + offset],
    //                                   [-20.90515744788931, 0 + offset],
    //                                   [-45 + offset, 35.264389682754654],
    //                                   [0, 69.09484255211069 - offset]];
    //
    //             var hedralPoints12 = [[45, 35.264389682754654 - offset],
    //                                   [90 - offset, 20.90515744788931],
    //                                   [90 - offset, -20.90515744788931],
    //                                   [45, -35.264389682754654 + offset],
    //                                   [20.90515744788931 + offset, 0],
    //                                   [45, 35.264389682754654 - offset]];
    //
    //             var hedralPoints15 = [[90 + offset, 20.90515744788931],
    //                                   [135, 35.264389682754654 - offset],
    //                                   [159.09484255211072 - offset, 0],
    //                                   [135 + offset, -35.264389682754654],
    //                                   [90 + offset, -20.90515744788931],
    //                                   [90 + offset, 20.90515744788931]];
    //
    //             var hedralPoints211 = [[-135 - offset, 35.264389682754654],
    //                                    [-90 - offset, 20.90515744788931],
    //                                    [-90 - offset, -20.90515744788931],
    //                                    [-135, -35.264389682754654 + offset],
    //                                    [-159.09484255211072 + offset, 0],
    //                                    [-135 - offset, 35.264389682754654]];
    //
    //             var hedralPoints28 = [[-45, 35.264389682754654 + offset],
    //                                   [-20.90515744788931 - offset, 0],
    //                                   [-45, -35.264389682754654 + offset],
    //                                   [-90 + offset, -20.90515744788931],
    //                                   [-90, 20.90515744788931 - offset],
    //                                   [-45, 35.264389682754654 + offset]];
    //
    //             var hedralPoints24 = [[20.90515744788931, 0 - offset],
    //                                   [45 - offset, -35.264389682754654],
    //                                   [0, -69.09484255211069 + offset],
    //                                   [-45 + offset, -35.264389682754654],
    //                                   [-20.90515744788931, 0 - offset],
    //                                   [20.90515744788931, 0 - offset]];
    //
    //             var hedralPoints26 = [[90, -20.90515744788931 - offset],
    //                                   [135 - offset, -35.264389682754654],
    //                                   [180 - offset, -69.09484255211069],
    //                                   [0 + offset, -69.09484255211069],
    //                                   [45, -35.264389682754654 - offset],
    //                                   [90, -20.90515744788931 - offset]];
    //
    //              var hedralPoints210 = [[159.09484255211072, 0 - offset],
    //                                     [-159.09484255211072, 0 - offset],
    //                                     [-135 - offset, -35.264389682754654],
    //                                     [180, -69.09484255211069 + offset],
    //                                     [135 + offset, -35.264389682754654],
    //                                     [159.09484255211072, 0 - offset]];
    //
    //
    //             var hedralPointsArr = [];
    //             hedralPointsArr.push(hedralPoints00);
    //             hedralPointsArr.push(hedralPoints17);
    //             hedralPointsArr.push(hedralPoints13);
    //             hedralPointsArr.push(hedralPoints11);
    //             hedralPointsArr.push(hedralPoints12);
    //             hedralPointsArr.push(hedralPoints15);
    //             hedralPointsArr.push(hedralPoints211);
    //             hedralPointsArr.push(hedralPoints28);
    //             hedralPointsArr.push(hedralPoints24);
    //             hedralPointsArr.push(hedralPoints26);
    //             hedralPointsArr.push(hedralPoints210);
    //
    //             var center = this.center(µ.view());
    //             var wingProjection = geoDodecahedralN().scale(currentScale).translate(projection([150, 0])).rotate([0, 0, 0]);
    //
    //             var xOffset = 3, yOffset = 2;
    //
    //             context.fillStyle = context.strokeStyle = "#000005";
    //             // context.fillStyle = context.strokeStyle = "#c4c9d1";
    //             context.lineWidth = 0.5;
    //             context.beginPath();
    //
    //             var hedralCoords = [];
    //
    //             // 0-0
    //             for (var i=0; i<hedralPointsArr[0].length; i++){
    //                 hedralCoords.push(wingProjection(hedralPointsArr[0][i]));
    //             }
    //
    //             // drawWingLine(context, hedralCoords);
    //             drawWingLineType0(context, hedralCoords);
    //             drawWingLineType4(context, hedralCoords);
    //
    //             // 1-7
    //             hedralCoords = [];
    //             for (var i=0; i<hedralPointsArr[1].length; i++){
    //                 hedralCoords.push(wingProjection(hedralPointsArr[1][i]));
    //             }
    //
    //             drawWingLineType1(context, hedralCoords);
    //             drawWingLineType2(context, hedralCoords);
    //             drawWingLineType3(context, hedralCoords);
    //
    //             // 1-3
    //             hedralCoords = [];
    //             for (var i=0; i<hedralPointsArr[2].length; i++){
    //                 hedralCoords.push(wingProjection(hedralPointsArr[2][i]));
    //             }
    //
    //             drawWingLineType2(context, hedralCoords);
    //
    //             // 1-1
    //             hedralCoords = [];
    //             for (var i=0; i<hedralPointsArr[3].length; i++){
    //                 hedralCoords.push(wingProjection(hedralPointsArr[3][i]));
    //             }
    //
    //             drawWingLineType2(context, hedralCoords);
    //
    //             // 1-2
    //             hedralCoords = [];
    //             for (var i=0; i<hedralPointsArr[4].length; i++){
    //                 hedralCoords.push(wingProjection(hedralPointsArr[4][i]));
    //             }
    //
    //             drawWingLineType1(context, hedralCoords);
    //             drawWingLineType2(context, hedralCoords);
    //
    //             // 1-5
    //             hedralCoords = [];
    //             for (var i=0; i<hedralPointsArr[5].length; i++){
    //                 hedralCoords.push(wingProjection(hedralPointsArr[5][i]));
    //             }
    //
    //             drawWingLineType21(context, hedralCoords);
    //
    //             // 2-11
    //             hedralCoords = [];
    //             for (var i=0; i<hedralPointsArr[6].length; i++){
    //                 hedralCoords.push(wingProjection(hedralPointsArr[6][i]));
    //             }
    //
    //             drawWingLineType4(context, hedralCoords);
    //             drawWingLineType5(context, hedralCoords);
    //
    //             // 2-8
    //             hedralCoords = [];
    //             for (var i=0; i<hedralPointsArr[7].length; i++){
    //                 hedralCoords.push(wingProjection(hedralPointsArr[7][i]));
    //             }
    //
    //             drawWingLineType5(context, hedralCoords);
    //             drawWingLineType6(context, hedralCoords);
    //
    //             // 2-4
    //             hedralCoords = [];
    //             for (var i=0; i<hedralPointsArr[8].length; i++){
    //                 hedralCoords.push(wingProjection(hedralPointsArr[8][i]));
    //             }
    //
    //             drawWingLineType6(context, hedralCoords);
    //
    //             // 2-6
    //             hedralCoords = [];
    //             for (var i=0; i<hedralPointsArr[9].length; i++){
    //                 hedralCoords.push(wingProjection(hedralPointsArr[9][i]));
    //             }
    //
    //             drawWingLineType5(context, hedralCoords);
    //             drawWingLineType6(context, hedralCoords);
    //
    //             // 2-10
    //             hedralCoords = [];
    //             for (var i=0; i<hedralPointsArr[10].length; i++){
    //                 hedralCoords.push(wingProjection(hedralPointsArr[10][i]));
    //             }
    //
    //             drawWingLineType5(context, hedralCoords);
    //             drawWingLineType6(context, hedralCoords);
    //
    //             context.stroke();
    //
    //             function drawWingLine(context, hedralCoords){
    //                 context.moveTo(hedralCoords[0][0], hedralCoords[0][1]);
    //                 context.lineTo(hedralCoords[1][0], hedralCoords[1][1]);
    //                 context.lineTo(hedralCoords[2][0], hedralCoords[2][1]);
    //                 context.lineTo(hedralCoords[3][0], hedralCoords[3][1]);
    //                 context.lineTo(hedralCoords[4][0], hedralCoords[4][1]);
    //                 context.lineTo(hedralCoords[5][0], hedralCoords[5][1]);
    //             }
    //
    //             function drawWingLineType0(context, hedralCoords){
    //                 context.moveTo(hedralCoords[0][0] + xOffset *  0,      hedralCoords[0][1] + yOffset * 2);
    //                 context.lineTo(hedralCoords[0][0] + xOffset *  1 + 30, hedralCoords[0][1] + yOffset *  2 - 28);
    //                 context.lineTo(hedralCoords[1][0] + xOffset * -1 + 16,  hedralCoords[1][1] + yOffset *  1 - 40);
    //                 context.lineTo(hedralCoords[1][0] + xOffset * -1,      hedralCoords[1][1] + yOffset *  1);
    //             }
    //
    //             function drawWingLineType1(context, hedralCoords){
    //                 context.moveTo(hedralCoords[0][0] + xOffset *  1,     hedralCoords[0][1] + yOffset *  1);
    //                 context.lineTo(hedralCoords[0][0] + xOffset *  1 + 16, hedralCoords[0][1] + yOffset *  1 - 40);
    //                 context.lineTo(hedralCoords[1][0] + xOffset * -1 - 16, hedralCoords[1][1] + yOffset *  1 - 40);
    //                 context.lineTo(hedralCoords[1][0] + xOffset * -1,     hedralCoords[1][1] + yOffset *  1);
    //             }
    //
    //             function drawWingLineType2(context, hedralCoords){
    //                 context.moveTo(hedralCoords[1][0] + xOffset *  0,      hedralCoords[1][1] + yOffset *  2);
    //                 context.lineTo(hedralCoords[1][0] + xOffset *  0 + 40, hedralCoords[1][1] + yOffset *  2);
    //                 context.lineTo(hedralCoords[2][0] + xOffset *  0 + 20, hedralCoords[2][1] + yOffset *  1 - 62);
    //                 context.lineTo(hedralCoords[2][0] + xOffset *  0,      hedralCoords[2][1] + yOffset *  1);
    //             }
    //
    //             function drawWingLineType21(context, hedralCoords){
    //                 context.moveTo(hedralCoords[1][0] + xOffset *  0,      hedralCoords[1][1] + yOffset *  2);
    //                 context.lineTo(hedralCoords[1][0] + xOffset *  0 + 40, hedralCoords[1][1] + yOffset *  2);
    //                 context.lineTo(hedralCoords[2][0] + xOffset *  0 + 30, hedralCoords[2][1] + yOffset *  1 - 24);
    //                 context.lineTo(hedralCoords[2][0] + xOffset *  0,      hedralCoords[2][1] + yOffset *  1);
    //             }
    //
    //             function drawWingLineType3(context, hedralCoords){
    //                 context.moveTo(hedralCoords[3][0] + xOffset *  0,      hedralCoords[3][1] + yOffset *  -1);
    //                 context.lineTo(hedralCoords[3][0] + xOffset *  0 - 32, hedralCoords[3][1] + yOffset *  -1 + 22);
    //                 context.lineTo(hedralCoords[4][0] + xOffset *  1 - 12, hedralCoords[4][1] + yOffset *  1 + 32);
    //                 context.lineTo(hedralCoords[4][0] + xOffset *  1,      hedralCoords[4][1] + yOffset *  0);
    //             }
    //
    //             function drawWingLineType4(context, hedralCoords){
    //                 context.moveTo(hedralCoords[4][0] + xOffset *  1,      hedralCoords[4][1] + yOffset *  1);
    //                 context.lineTo(hedralCoords[4][0] + xOffset *  1 - 32, hedralCoords[4][1] + yOffset *  1 + 22);
    //                 context.lineTo(hedralCoords[3][0] + xOffset * -1 - 32, hedralCoords[3][1] + yOffset * -2 + 0);
    //                 context.lineTo(hedralCoords[3][0] + xOffset *  1,      hedralCoords[3][1] + yOffset * -2);
    //             }
    //
    //             function drawWingLineType5(context, hedralCoords){
    //                 context.moveTo(hedralCoords[2][0] + xOffset * -1,      hedralCoords[2][1] + yOffset * -1);
    //                 context.lineTo(hedralCoords[2][0] + xOffset *  0 - 20, hedralCoords[2][1] + yOffset * -1 + 40);
    //                 context.lineTo(hedralCoords[3][0] + xOffset *  0 + 20, hedralCoords[3][1] + yOffset * -1 + 40);
    //                 context.lineTo(hedralCoords[3][0] + xOffset *  1,      hedralCoords[3][1] + yOffset * -1);
    //             }
    //
    //             function drawWingLineType6(context, hedralCoords){
    //                 context.moveTo(hedralCoords[3][0] + xOffset *  1,      hedralCoords[3][1] + yOffset * -2);
    //                 context.lineTo(hedralCoords[3][0] + xOffset *  0 - 40, hedralCoords[3][1] + yOffset * -2);
    //                 context.lineTo(hedralCoords[4][0] + xOffset *  0 - 22, hedralCoords[4][1] + yOffset *  1 + 64);
    //                 context.lineTo(hedralCoords[4][0] + xOffset *  0,      hedralCoords[4][1] + yOffset *  -1);
    //             }
    //         }
    //     });
    // }

    return d3.map({
        atlantis: atlantis,
        azimuthal_equidistant: azimuthalEquidistant,
        conic_equidistant: conicEquidistant,
        equirectangular: equirectangular,
        orthographic: orthographic,
        stereographic: stereographic,
        waterman: waterman,
        winkel3: winkel3
        // cubic: cubic,
        // dodecahedral: dodecahedral
    });

})();
